import React, { useCallback, useEffect, useState } from "react";
import {
  DialogActions,
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { generateTempCode } from "../../slices/operaters";
import {
  enqueueSnackbar as enqueueSnackbarAction,
  closeSnackbar as closeSnackbarAction,
} from "../../slices/notifier";
import { useTranslation } from "react-i18next";

// Formats a remaining-seconds count as "M:SS" (e.g. 299 -> "4:59").
const formatCountdown = (totalSeconds) => {
  const safe = Math.max(0, totalSeconds);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

// Super-admin modal that issues a single-use temporary 2FA code for an operator
// who lost their authenticator. The code lives only in this component's memory
// (it is cleared on close) and is never written to storage. A live countdown
// mirrors the backend `expiresInSeconds`; once it hits 0 the code is greyed out
// and the admin can issue a fresh one.
const TempCodeModal = ({ id, open, handleClose, data }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { statusTempCode } = useSelector((state) => state.operators);
  const [code, setCode] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [copied, setCopied] = useState(false);

  const enqueueSnackbar = (...args) => dispatch(enqueueSnackbarAction(...args));
  const closeSnackbar = (...args) => dispatch(closeSnackbarAction(...args));
  const showSnackbar = (snack) => {
    enqueueSnackbar({
      message: t(snack.message),
      options: {
        key: new Date().getTime() + Math.random(),
        variant: snack.variant,
        action: (key) => (
          <Button style={{ color: "#fff" }} onClick={() => closeSnackbar(key)}>
            {t("dismiss me")}
          </Button>
        ),
      },
    });
  };

  const loading = statusTempCode === "loading";
  const expired = Boolean(code) && secondsLeft <= 0;

  const generate = useCallback(() => {
    if (!data?.operatorId || !data?.websiteId) {
      return;
    }
    setCopied(false);
    dispatch(
      generateTempCode({
        operatorId: data.operatorId,
        websiteID: data.websiteId,
      })
    ).then((res) => {
      if (res?.error) {
        // e.g. operator has no 2FA set up — surface the backend message.
        showSnackbar({
          variant: "error",
          message: res?.error?.message || t("Something went wrong"),
        });
        handleClose(id);
        return;
      }
      const payload = res?.payload?.data;
      setCode(payload?.code || null);
      setSecondsLeft(payload?.expiresInSeconds || 0);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dispatch, id]);

  // Generate a code the first time the modal opens, and wipe everything on close
  // so the code never lingers in memory between operators.
  useEffect(() => {
    if (open && data?.operatorId && !code && !loading) {
      generate();
    }
    if (!open) {
      setCode(null);
      setSecondsLeft(0);
      setCopied(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Tick the countdown down to 0. The timeout is re-armed on each second via the
  // `secondsLeft` dependency and naturally stops once it reaches 0.
  useEffect(() => {
    if (!open || secondsLeft <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearTimeout(timer);
  }, [open, secondsLeft]);

  const handleCopy = async () => {
    if (!code) {
      return;
    }
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      showSnackbar({ variant: "error", message: t("Could not copy code") });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => handleClose(id)}
      className="info-modal temp-code-modal"
    >
      <DialogTitle className="assign-operator-title">
        {t("Temporary login code")}
      </DialogTitle>
      <DialogContent>
        <p className="temp-code-instructions">
          {t(
            "Share this code with the operator by phone or chat. It is valid for 5 minutes and can be used only once."
          )}
        </p>

        <div className={`temp-code-display${expired ? " expired" : ""}`}>
          {loading && !code ? (
            <CircularProgress className="temp-code-spinner" />
          ) : (
            <span className="temp-code-value">{code || "------"}</span>
          )}
          <Button
            type="button"
            className="temp-code-copy-btn"
            onClick={handleCopy}
            disabled={!code || expired}
          >
            {copied ? t("Copied") : t("Copy")}
          </Button>
        </div>

        <p className={`temp-code-countdown${expired ? " expired" : ""}`}>
          {expired
            ? t("Code expired")
            : `${t("Expires in")} ${formatCountdown(secondsLeft)}`}
        </p>
      </DialogContent>
      <DialogActions className="assign-operator-btns">
        <Button
          className="assign-operator-cancel-btn"
          onClick={() => handleClose(id)}
        >
          {t("Close")}
        </Button>
        {expired && (
          <Button
            className="delete-message-btn"
            disabled={loading}
            onClick={generate}
          >
            {loading ? (
              <CircularProgress className="circular-progress-delete-modal" />
            ) : (
              t("Generate again")
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TempCodeModal;
